import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import { useAuth } from '../context/AuthContext';
import { isAvailable, isFieldUsable, daysCheckedOut } from '../lib/assetStatus';
import ChecklistRenderer, { flattenItems, initialValuesFor } from './ChecklistRenderer';
import JobSelectionCard from './JobSelectionCard';
import PhotoLightbox from './PhotoLightbox';
import { uploadAssetChecklistPhoto } from '../lib/assetChecklistUploads';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Plus, 
  Trash2,
  Loader2,
  Truck,
  QrCode,
  Download,
  Edit,
  Camera,
  X,
  LogIn,
  LogOut,
  History,
  Search,
  CheckCircle2,
  AlertCircle,
  Clock,
  Package,
  ScanLine,
  Zap,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  User,
  Users,
  ArrowRight,
  Bell
} from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import QRScanner from './QRScanner';
import { ViewModeToggle } from './ViewModeToggle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status badge colors
const STATUS_COLORS = {
  'Active': 'bg-green-100 text-green-800 border-green-200',
  'Available': 'bg-green-100 text-green-800 border-green-200',  // legacy alias
  'Checked Out': 'bg-amber-100 text-amber-800 border-amber-200',
  'In Service': 'bg-blue-100 text-blue-800 border-blue-200',
  'Retired': 'bg-slate-100 text-slate-600 border-slate-200'
};

// Category icons (using Truck as default)
const CATEGORY_ICONS = {
  'Trucks': Truck,
  'Vac Truck': Truck,
  'Trailer': Package,
  'Camera': Camera,
  'Generators': Package,
  'Drone': Package,
  'ATV': Truck
};

// Tiny sort-arrow indicator next to a sortable column header. Defined at
// module scope (not inside AssetManagement) so React doesn't re-mount it on
// every render — that was breaking column-click sort animations.
const SortIcon = ({ col, sortBy, sortDir }) => {
  if (sortBy !== col) return <ArrowUpDown className="inline w-3 h-3 ml-1 opacity-30" />;
  return sortDir === 'asc'
    ? <ChevronUp className="inline w-3 h-3 ml-1" />
    : <ChevronDown className="inline w-3 h-3 ml-1" />;
};

export default function AssetManagement() {
  const { user, token, updatePreference } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Filters — persisted server-side per user, cross-device (see effect
  // below), so this initial state reads whatever was saved last.
  const savedEquipPrefs = user?.preferences?.equipment_page_filters || {};
  const [filterCategory, setFilterCategory] = useState(savedEquipPrefs.filterCategory ?? 'all');
  const [filterStatus, setFilterStatus] = useState(savedEquipPrefs.filterStatus ?? 'all');
  // 2026-09-06: searchQuery used to be persisted the same way as the
  // other filters. Root cause of "stats say 1, list says No assets
  // found with all filters cleared": a stale search term from an
  // earlier session ("TEST_iter176") was silently saved server-side and
  // kept being restored on every future page load, filtering out every
  // real asset while the stat tiles (computed from the unfiltered
  // `assets` array, correctly) kept showing the true count. Search text
  // is transient/contextual, unlike category/status/sort/view — it no
  // longer persists across sessions, always starting blank.
  const [searchQuery, setSearchQuery] = useState('');
  // Column sort: click header to toggle asc/desc. `null` = no sort (server order).
  const [sortBy, setSortBy] = useState(savedEquipPrefs.sortBy ?? null);   // 'asset_number' | 'name' | 'category' | 'makemodel' | 'status' | 'holder'
  const [sortDir, setSortDir] = useState(savedEquipPrefs.sortDir ?? 'asc'); // 'asc' | 'desc'
  const [viewMode, setViewMode] = useState(() => {
    if (savedEquipPrefs.viewMode === 'cards' || savedEquipPrefs.viewMode === 'list') return savedEquipPrefs.viewMode;
    // Pre-migration fallback for users who set this before server-side
    // persistence existed — the save effect below promotes it to the
    // server automatically on next mount, no server value ever wins.
    try {
      const legacy = localStorage.getItem('equipment.viewMode');
      if (legacy === 'cards' || legacy === 'list') return legacy;
    } catch {}
    return 'cards';
  });

  // Debounced save-on-change to PUT /auth/preferences (merge-based — only
  // touches the `equipment_page_filters` key, other saved preferences like
  // calendar_layout are untouched). Also runs on mount, which is what
  // migrates a legacy localStorage-only viewMode to the server the first
  // time this page loads for a user who doesn't have the key yet.
  useEffect(() => {
    const handle = setTimeout(() => {
      updatePreference('equipment_page_filters', {
        filterCategory, filterStatus, sortBy, sortDir, viewMode,
      });
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCategory, filterStatus, sortBy, sortDir, viewMode]);
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);
  const [showCheckinDialog, setShowCheckinDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  // Blob URL for live-generated QR code when the asset has no stored
  // qr_code_url (recovery-migrated assets). Cleared when dialog closes.
  const [liveQrBlobUrl, setLiveQrBlobUrl] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  
  // Quick Action modal
  const [showQuickActionModal, setShowQuickActionModal] = useState(false);
  const [quickActionMode, setQuickActionMode] = useState('checkout'); // 'checkout' or 'checkin'
  const [quickSearchQuery, setQuickSearchQuery] = useState('');
  const [showAllCheckedOut, setShowAllCheckedOut] = useState(false);
  const [pendingQuickAction, setPendingQuickAction] = useState(null); // Store pending action until assets load
  
  // Handle URL query parameter for quick action (from Dashboard)
  useEffect(() => {
    const quickAction = searchParams.get('quickAction');
    if (quickAction === 'checkout' || quickAction === 'checkin') {
      // Store the action to be opened once assets are loaded
      setPendingQuickAction(quickAction);
      // Clear the query parameter
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  
  // Open quick action modal once assets are loaded and there's a pending action
  useEffect(() => {
    if (pendingQuickAction && !loading && assets.length >= 0) {
      setQuickActionMode(pendingQuickAction);
      setQuickSearchQuery('');
      setShowAllCheckedOut(false);
      setShowQuickActionModal(true);
      setPendingQuickAction(null);
    }
  }, [pendingQuickAction, loading, assets.length]);
  
  // Recently Used feature - load preference from localStorage
  const [showRecentlyUsed, setShowRecentlyUsed] = useState(() => {
    const saved = localStorage.getItem('fieldready_show_recent_equipment');
    return saved !== null ? JSON.parse(saved) : true; // Default to ON
  });
  const [recentlyUsedIds, setRecentlyUsedIds] = useState(() => {
    const saved = localStorage.getItem('fieldready_recent_equipment');
    return saved ? JSON.parse(saved) : [];
  });
  
  // Selected asset
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetHistory, setAssetHistory] = useState([]);
  
  // Create/Edit form state
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    year: '',
    make: '',
    model: '',
    serial_number: '',
    vin: '',
    license_plate: '',
    color: '',
    purchase_date: '',
    notes: '',
    // Feb-2026 round 4: default true so newly-added trucks/equipment
    // show up in the Job → Assign Equipment picker automatically.
    // Owner (Chan) unchecks the ~11 personal trucks manually.
    assignable_to_jobs: true,
    // Round 7: default true so new equipment shows in Check-Out/
    // Check-In. Owner unchecks personal trucks that share a category
    // with Flex Vehicles.
    field_use: true,
    custom_field_values: {},
    // Aug-2026: only meaningful when editing an existing asset (see
    // handleEdit) — new equipment always starts "Active" server-side.
    status: 'Active',
    in_shop_note: ''
  });

  // Equipment categories (with custom_fields config) + team members for assignee picker
  const [equipmentCategories, setEquipmentCategories] = useState([]);
  // All active subcategories (flat list, filtered by parent client-side).
  // Feb 2026 — powers the required Subcategory picker in the Asset form
  // for any parent category that has ≥1 subcategory.
  const [equipmentSubcategories, setEquipmentSubcategories] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  // Active-asset list used by ChecklistRenderer to hydrate `select`
  // items whose seeded JSON has no options (e.g., "Select Truck Being
  // Inspected"). Same source as the main assets list; kept separate so
  // filters on the primary list don't affect the checklist picker.
  // Filtered by isFieldUsable so an asset/truck with "Available for
  // Check-Out/Check-In" unchecked (asset-level field_use=false, or a
  // shop-only category) is excluded from this dropdown too.
  const [assetsForSelect, setAssetsForSelect] = useState([]);
  // Job Selection for the Check-Out dialog (parity with the Check-Out
  // dialog on AssetDetail.js / PreCon / Soft Dig / Locate / CSE, all
  // of which already use <JobSelectionCard>).
  const [availableJobs, setAvailableJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [selectedJobId, setSelectedJobId] = useState('');
  // "Your Jobs Today" — same source/logic as Time Tracker v2
  // (GET /time-blocks/assigned-jobs, no `include_all` so the backend
  // already filters to just this user's jobs for today).
  const [myJobIds, setMyJobIds] = useState([]);
  useEffect(() => {
    let mounted = true;
    Promise.all([
      axios.get(`${API}/assets/equipment-categories`).catch(() => ({ data: [] })),
      axios.get(`${API}/team`).catch(() => ({ data: [] })),
      axios.get(`${API}/equipment-subcategories`).catch(() => ({ data: [] })),
      axios.get(`${API}/assets`).catch(() => ({ data: [] })),
      axios.get(`${API}/jobs/for-field-reports`).catch(() => ({ data: [] })),
      axios.get(`${API}/time-blocks/assigned-jobs?tz_offset=${new Date().getTimezoneOffset()}`).catch(() => ({ data: [] })),
    ]).then(([cat, tm, subs, as, jobs, myJobs]) => {
      if (!mounted) return;
      const categories = cat.data || [];
      setEquipmentCategories(categories);
      setTeamMembers(tm.data || []);
      setEquipmentSubcategories(Array.isArray(subs.data) ? subs.data : []);
      setAssetsForSelect(
        (Array.isArray(as.data) ? as.data : [])
          .filter(a => a && a.name)
          .filter(a => isFieldUsable(a, categories))
      );
      setAvailableJobs(Array.isArray(jobs.data) ? jobs.data : []);
      setMyJobIds((Array.isArray(myJobs.data) ? myJobs.data : []).map(j => j.id));
    }).finally(() => {
      if (mounted) setLoadingJobs(false);
    });
    return () => { mounted = false; };
  }, []);
  const selectedJob = availableJobs.find(j => j.id === selectedJobId) || null;
  const jobPayload = () => (selectedJob ? {
    job_id: selectedJob.id,
    job_number: selectedJob.job_number,
    job_service_address: selectedJob.service_address || '',
    job_date: selectedJob.date || '',
    job_assigned_team: selectedJob.assigned_team_names || [],
  } : {});

  // Subcategories filtered by the currently-selected parent category
  // name. Case-insensitive match — parent_category_name on each sub
  // is denormalized so no join is needed.
  const subcategoriesForCurrentCategory = () => {
    const catName = (formData.category || '').trim().toLowerCase();
    if (!catName) return [];
    return equipmentSubcategories.filter(
      s => (s.parent_category_name || '').toLowerCase() === catName && s.active !== false,
    );
  };

  // Look up the configured custom_fields for a given asset category
  const getCustomFieldsForCategory = (catName) => {
    if (!catName) return [];
    const t = String(catName).trim().toLowerCase();
    const match = equipmentCategories.find(c => (c.name || '').toLowerCase() === t)
      || equipmentCategories.find(c => {
        const n = (c.name || '').toLowerCase();
        return n === t + 's' || t === n + 's' || n.startsWith(t) || t.startsWith(n);
      });
    return Array.isArray(match?.custom_fields) ? match.custom_fields : [];
  };
  
  // Checkout/Checkin checklist
  const [checklistItems, setChecklistItems] = useState([]);
  const [checklistValues, setChecklistValues] = useState({});
  const [checklistTitle, setChecklistTitle] = useState('');
  const [checklistSource, setChecklistSource] = useState('placeholder');
  const [checklistUpdatedAt, setChecklistUpdatedAt] = useState(null);
  
  // Signature canvas
  const signatureCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  
  // Photo upload refs
  const photoInputRefs = useRef({});
  const [uploadingPhoto, setUploadingPhoto] = useState(null);

  // Lightbox state — shared across every dialog that shows checklist
  // thumbnails so mobile / desktop users can tap-to-enlarge.
  const [lightboxPhotos, setLightboxPhotos] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const openLightbox = (photos, idx) => {
    setLightboxPhotos(photos || []);
    setLightboxIndex(idx || 0);
    setLightboxOpen(true);
  };

  // Check if user is admin/manager
  const canManageAssets = user?.role === 'admin' || user?.role === 'super_user' || (user?.permissions || {}).manage_equipment === true;
  // Overdue Checkout Alerts — who can send the manual "Send Reminder" nudge
  // (mirrors the backend's role gate in POST /assets/{id}/send-reminder).
  const canSendReminder = ['admin', 'manager', 'super_user'].includes(user?.role);
  const [overdueThreshold, setOverdueThreshold] = useState(3);
  const [sendingReminderId, setSendingReminderId] = useState(null);
  const [sendingAllReminders, setSendingAllReminders] = useState(false);
  const REMINDER_COOLDOWN_MINUTES = 60;
  const overdueAssetsList = assets.filter(a => {
    const d = daysCheckedOut(a);
    const onCooldown = (a.reminder_cooldown_seconds_remaining || 0) > 0;
    return d !== null && d >= overdueThreshold && a.current_holder_id && !onCooldown;
  });
  const handleSendAllReminders = async () => {
    setSendingAllReminders(true);
    const remindedIds = overdueAssetsList.map(a => a.id);
    try {
      const res = await axios.post(`${API}/assets/send-all-reminders`);
      toast.success(res.data?.message || 'Reminders sent');
      setAssets(prev => prev.map(a => remindedIds.includes(a.id)
        ? { ...a, reminder_cooldown_seconds_remaining: REMINDER_COOLDOWN_MINUTES * 60 }
        : a));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSendingAllReminders(false);
    }
  };
  useEffect(() => {
    axios.get(`${API}/assets/overdue-settings`)
      .then(r => setOverdueThreshold(r.data?.days_threshold ?? 3))
      .catch(() => {});
  }, []);
  const handleSendReminder = async (asset) => {
    setSendingReminderId(asset.id);
    try {
      const res = await axios.post(`${API}/assets/${asset.id}/send-reminder`);
      toast.success(res.data?.message || 'Reminder sent');
      // Optimistic update so the cooldown kicks in immediately without
      // waiting for the next full assets refetch.
      setAssets(prev => prev.map(a => a.id === asset.id
        ? { ...a, reminder_cooldown_seconds_remaining: REMINDER_COOLDOWN_MINUTES * 60 }
        : a));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminderId(null);
    }
  };
  // Equipment list category accordion — was uncontrolled (defaultValue
  // recomputed every render but only honored once by Radix), always
  // snapping back open on the first alphabetical category. Fixed using
  // the same controlled + localStorage pattern already proven on the
  // Job page's "Assign Equipment" accordion (JobDetail.js).
  const [equipmentOpenCats, setEquipmentOpenCats] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('equipment.openCategories'));
      return Array.isArray(saved) ? saved : [];
    } catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem('equipment.openCategories', JSON.stringify(equipmentOpenCats)); } catch {}
  }, [equipmentOpenCats]);
  // Same "opened by you" stamping as JobDetail.js's Assign Equipment
  // accordion, so a category left open days ago doesn't look like a
  // mystery default.
  const [equipmentOpenedAt, setEquipmentOpenedAt] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('equipment.openCategoriesOpenedAt'));
      return saved && typeof saved === 'object' ? saved : {};
    } catch { return {}; }
  });
  const handleEquipmentOpenChange = (newOpenCats) => {
    setEquipmentOpenedAt(prev => {
      const next = {};
      newOpenCats.forEach(cat => { next[cat] = prev[cat] || new Date().toISOString(); });
      try { localStorage.setItem('equipment.openCategoriesOpenedAt', JSON.stringify(next)); } catch {}
      return next;
    });
    setEquipmentOpenCats(newOpenCats);
  };

  // Toggle recently used preference and save to localStorage
  const toggleRecentlyUsed = (value) => {
    setShowRecentlyUsed(value);
    localStorage.setItem('fieldready_show_recent_equipment', JSON.stringify(value));
  };

  // Add equipment to recently used list (called after checkout/checkin)
  const addToRecentlyUsed = useCallback((assetId) => {
    setRecentlyUsedIds(prev => {
      // Remove if already exists, then add to front
      const filtered = prev.filter(id => id !== assetId);
      const updated = [assetId, ...filtered].slice(0, 5); // Keep max 5
      localStorage.setItem('fieldready_recent_equipment', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Get recently used assets (filter by current mode)
  const getRecentlyUsedAssets = useCallback(() => {
    if (!showRecentlyUsed || recentlyUsedIds.length === 0) return [];
    
    return recentlyUsedIds
      .map(id => assets.find(a => a.id === id))
      .filter(Boolean)
      .filter(asset => {
        if (quickActionMode === 'checkout') {
          return isAvailable(asset);
        } else {
          // Check In mode
          if (showAllCheckedOut) {
            return asset.status === 'Checked Out';
          }
          return asset.status === 'Checked Out' && asset.current_holder_id === user?.id;
        }
      });
  }, [showRecentlyUsed, recentlyUsedIds, assets, quickActionMode, showAllCheckedOut, user?.id]);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/assets/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, []);

  // Fetch assets
  const fetchAssets = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterCategory && filterCategory !== 'all') params.append('category', filterCategory);
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);
      
      const response = await axios.get(`${API}/assets?${params.toString()}`);
      setAssets(response.data);
    } catch (error) {
      console.error('Failed to fetch assets:', error);
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterStatus]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  // Filter assets by search query + apply column sort
  // #49 — "Clear filters" default snapshot, shared by the visibility
  // check and the reset handler so they can never drift apart.
  const EQUIPMENT_FILTER_DEFAULTS = { filterCategory: 'all', filterStatus: 'all', searchQuery: '', sortBy: null, sortDir: 'asc', viewMode: 'cards' };
  const isEquipmentFiltersDefault =
    filterCategory === EQUIPMENT_FILTER_DEFAULTS.filterCategory &&
    filterStatus === EQUIPMENT_FILTER_DEFAULTS.filterStatus &&
    searchQuery === EQUIPMENT_FILTER_DEFAULTS.searchQuery &&
    sortBy === EQUIPMENT_FILTER_DEFAULTS.sortBy &&
    sortDir === EQUIPMENT_FILTER_DEFAULTS.sortDir &&
    viewMode === EQUIPMENT_FILTER_DEFAULTS.viewMode;
  const clearEquipmentFilters = () => {
    setFilterCategory(EQUIPMENT_FILTER_DEFAULTS.filterCategory);
    setFilterStatus(EQUIPMENT_FILTER_DEFAULTS.filterStatus);
    setSearchQuery(EQUIPMENT_FILTER_DEFAULTS.searchQuery);
    setSortBy(EQUIPMENT_FILTER_DEFAULTS.sortBy);
    setSortDir(EQUIPMENT_FILTER_DEFAULTS.sortDir);
    setViewMode(EQUIPMENT_FILTER_DEFAULTS.viewMode);
    // State updates above already trigger the debounced save effect,
    // but save immediately too so a reset isn't left un-persisted if
    // the user navigates away inside the 400ms debounce window.
    updatePreference('equipment_page_filters', EQUIPMENT_FILTER_DEFAULTS);
  };

  const filteredAssets = (() => {
    const q = (searchQuery || '').toLowerCase().trim();
    const matchesSearch = (asset) => {
      if (!q) return true;
      return (
        asset.name?.toLowerCase().includes(q) ||
        asset.asset_number?.toLowerCase().includes(q) ||
        asset.vin?.toLowerCase().includes(q) ||
        asset.make?.toLowerCase().includes(q) ||
        asset.model?.toLowerCase().includes(q) ||
        asset.license_plate?.toLowerCase().includes(q) ||
        asset.serial_number?.toLowerCase().includes(q) ||
        asset.category?.toLowerCase().includes(q) ||
        asset.notes?.toLowerCase().includes(q) ||
        asset.current_holder?.toLowerCase().includes(q) ||
        String(asset.year || '').includes(q)
      );
    };
    const rows = assets.filter(matchesSearch);
    if (!sortBy) return rows;
    const dir = sortDir === 'desc' ? -1 : 1;
    const keyFn = (a) => {
      switch (sortBy) {
        case 'asset_number': return (a.asset_number || '').toLowerCase();
        case 'name':         return (a.name || '').toLowerCase();
        case 'category':     return (a.category || '').toLowerCase();
        case 'makemodel':    return `${a.year || ''} ${a.make || ''} ${a.model || ''}`.toLowerCase();
        case 'status':       return (a.status || '').toLowerCase();
        case 'holder':       return (a.current_holder || '').toLowerCase();
        default:             return '';
      }
    };
    return [...rows].sort((a, b) => {
      const ka = keyFn(a);
      const kb = keyFn(b);
      if (ka < kb) return -1 * dir;
      if (ka > kb) return 1 * dir;
      return 0;
    });
  })();

  const toggleSort = (col) => {
    if (sortBy === col) {
      // Click same column: asc → desc → off
      if (sortDir === 'asc') setSortDir('desc');
      else { setSortBy(null); setSortDir('asc'); }
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  // Round 7 — group the main equipment list by parent category →
  // subcategory, the same two-level shape the Check-Out/Check-In
  // picker already groups by (see the `groups[cat][sub]` accordion
  // further down this file). Collapses to the existing flat list
  // while actively searching, matching that picker's own convention.
  const assetGroups = (() => {
    if (searchQuery) return null;
    const groups = filteredAssets.reduce((acc, a) => {
      const cat = a.category || 'Uncategorized';
      const sub = a.subcategory_name || '(Unassigned)';
      acc[cat] = acc[cat] || {};
      (acc[cat][sub] = acc[cat][sub] || []).push(a);
      return acc;
    }, {});
    const groupNames = Object.keys(groups).sort((x, y) => x.localeCompare(y));
    const sortSubs = (subKeys) => [...subKeys].sort((x, y) => {
      if (x === '(Unassigned)') return 1;
      if (y === '(Unassigned)') return -1;
      return x.localeCompare(y);
    });
    return { groups, groupNames, sortSubs };
  })();

  // Extracted from the flat .map() so both the flat (search) path and
  // the grouped/accordion path can render the identical card/row.
  // Lightweight preview of the future Overdue Checkout Alerts feature —
  // color escalates the longer an asset has sat checked out, using the
  // same days_threshold configured in Settings > Equipment Categories.
  const CheckedOutDaysBadge = ({ asset, className = '' }) => {
    const days = daysCheckedOut(asset);
    if (days === null) return null;
    const tone = days >= overdueThreshold + 4 ? 'bg-red-100 text-red-700' : days >= overdueThreshold ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
    return (
      <span
        className={`text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap ${tone} ${className}`}
        data-testid={`days-checked-out-${asset.id}`}
        title={`Checked out for ${days} day${days === 1 ? '' : 's'}`}
      >
        {days === 0 ? 'OUT TODAY' : `OUT ${days}D`}
      </span>
    );
  };

  // One-click nudge to the holder, surfaced only once an asset crosses
  // the overdue threshold — separate from the automatic daily manager
  // alert (backend cron). Only managers/admins can send it.
  const SendReminderButton = ({ asset, className = '' }) => {
    const days = daysCheckedOut(asset);
    if (days === null || days < overdueThreshold || !canSendReminder || !asset.current_holder_id) return null;
    const secondsRemaining = asset.reminder_cooldown_seconds_remaining || 0;
    const onCooldown = secondsRemaining > 0;
    if (onCooldown) {
      const minsRemaining = Math.ceil(secondsRemaining / 60);
      const minsElapsed = REMINDER_COOLDOWN_MINUTES - minsRemaining;
      return (
        <span
          className={`text-[10px] text-slate-600 italic px-1.5 ${className}`}
          title="A reminder was already sent within the last hour"
          data-testid={`reminder-cooldown-${asset.id}`}
        >
          Reminded {minsElapsed <= 0 ? 'just now' : `${minsElapsed}m ago`}
        </span>
      );
    }
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => { e.stopPropagation(); handleSendReminder(asset); }}
        disabled={sendingReminderId === asset.id}
        className={`h-6 px-1.5 text-[10px] text-amber-700 hover:text-amber-800 hover:bg-amber-50 ${className}`}
        title={`Send a checkout reminder to ${asset.current_holder}`}
        data-testid={`send-reminder-${asset.id}`}
      >
        {sendingReminderId === asset.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Bell className="w-3 h-3 mr-1" />Remind</>}
      </Button>
    );
  };

  const renderAssetCardItem = (asset) => {
    const IconComponent = CATEGORY_ICONS[asset.category] || Package;
    return (
      <Card key={asset.id} className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`asset-card-${asset.id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-9 h-9 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                <IconComponent className="w-4 h-4 text-slate-600" />
              </div>
              <div className="min-w-0">
                <span className="font-mono text-xs text-amber-600 font-medium block">{asset.asset_number}</span>
                <h3 className="font-semibold text-slate-900 truncate">{asset.name}</h3>
              </div>
            </div>
            <Badge className={`${STATUS_COLORS[asset.status]} border text-xs flex-shrink-0`}>{asset.status}</Badge>
          </div>
          <div className="text-xs text-slate-500 space-y-0.5">
            <p><span className="text-slate-400">Category:</span> {asset.category}</p>
            {(asset.year || asset.make || asset.model) && (
              <p><span className="text-slate-400">Make/Model:</span> {asset.year ? `${asset.year} ` : ''}{asset.make} {asset.model}</p>
            )}
            <p><span className="text-slate-400">Holder:</span> {asset.current_holder || '—'} <CheckedOutDaysBadge asset={asset} /></p>
            <SendReminderButton asset={asset} className="-ml-1" />
          </div>
          <div className="flex items-center gap-1 flex-wrap pt-1 border-t border-slate-100">
            {isAvailable(asset) && isFieldUsable(asset, equipmentCategories) ? (
              <Button variant="ghost" size="sm" onClick={() => handleOpenCheckout(asset)} className="h-8 px-2 text-green-600 hover:text-green-700 hover:bg-green-50" title="Check Out" data-testid={`checkout-card-${asset.id}`}>
                <LogOut className="w-4 h-4 mr-1" /><span className="text-xs">Out</span>
              </Button>
            ) : asset.status === 'Checked Out' && isFieldUsable(asset, equipmentCategories) ? (
              <Button variant="ghost" size="sm" onClick={() => handleOpenCheckin(asset)} className="h-8 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50" title="Check In" data-testid={`checkin-card-${asset.id}`}>
                <LogIn className="w-4 h-4 mr-1" /><span className="text-xs">In</span>
              </Button>
            ) : null}
            <Button variant="ghost" size="sm" onClick={() => handleViewQR(asset)} className="h-8 w-8 p-0" title="QR Code"><QrCode className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" onClick={() => handleViewHistory(asset)} className="h-8 w-8 p-0" title="History"><History className="w-4 h-4" /></Button>
            {canManageAssets && (
              <>
                <Button variant="ghost" size="sm" onClick={() => handleEdit(asset)} className="h-8 w-8 p-0" title="Edit" data-testid={`edit-asset-card-${asset.id}`}><Edit className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => { setSelectedAsset(asset); setShowDeleteDialog(true); }} className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" title="Delete" data-testid={`delete-asset-card-${asset.id}`}><Trash2 className="w-4 h-4" /></Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  // Shared column widths for the accordion/grouped equipment table — every
  // sub-type's mini-table (e.g. Pole Camera, Push Camera, Rover Camera
  // under "Cameras") uses these same percentages so columns line up
  // identically across every sub-table in every category section, instead
  // of each mini-table auto-sizing its own columns from its own content.
  const GROUP_TABLE_COL_WIDTHS = {
    assetNumber: 'w-[18%]',
    name: 'w-[26%]',
    makeModel: 'w-[22%]',
    status: 'w-[12%]',
    holder: 'w-[14%]',
    actions: 'w-[16%]',
  };

  const renderAssetRowItem = (asset, opts = {}) => {
    // `hideCategory` + `colWidths` are only passed by the accordion/grouped
    // table (renderGroupTable) — its header row has no Category column
    // (redundant inside a category section) and needs fixed widths so every
    // sub-type's mini-table (Pole Camera, Push Camera, ...) lines up under
    // the exact same column boundaries. The flat/ungrouped table below keeps
    // calling this with no opts, so its Category column + auto widths are
    // untouched (2026-09-08 owner report: sub-tables were misaligned).
    const { hideCategory = false, colWidths = {} } = opts;
    const IconComponent = CATEGORY_ICONS[asset.category] || Package;
    return (
      <TableRow key={asset.id} data-testid={`asset-row-${asset.id}`}>
        <TableCell className={colWidths.assetNumber}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-slate-100 rounded flex items-center justify-center">
              <IconComponent className="w-4 h-4 text-slate-600" />
            </div>
            <span className="font-mono text-xs text-amber-600 font-medium">
              {asset.asset_number}
            </span>
          </div>
        </TableCell>
        <TableCell className={colWidths.name}>
          <div className="flex flex-col">
            <span className="font-medium text-slate-900">{asset.name}</span>
            <span className="text-xs text-slate-500 md:hidden">{asset.category}</span>
            {(() => {
              const fields = getCustomFieldsForCategory(asset.category).filter(f => f.field_type === 'date' && f.reminder_days_before != null);
              if (!fields.length) return null;
              const cfv = asset.custom_field_values || {};
              const today = new Date(); today.setHours(0,0,0,0);
              const items = fields.map(f => {
                const raw = cfv[f.id];
                const dateStr = raw && typeof raw === 'object' ? raw.date : raw;
                if (!dateStr) return null;
                const d = new Date(dateStr); d.setHours(0,0,0,0);
                const days = Math.round((d - today) / 86400000);
                if (days > f.reminder_days_before) return null; // not yet in window
                return { f, days, dateStr };
              }).filter(Boolean);
              if (!items.length) return null;
              return (
                <div className="mt-1 flex flex-wrap gap-1">
                  {items.map(({ f, days }) => (
                    <span
                      key={f.id}
                      className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${days < 0 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}
                      title={f.label}
                    >
                      {f.label}: {days < 0 ? `${Math.abs(days)}D OVERDUE` : days === 0 ? 'TODAY' : `IN ${days}D`}
                    </span>
                  ))}
                </div>
              );
            })()}
          </div>
        </TableCell>
        {!hideCategory && (
          <TableCell className="hidden md:table-cell">
            <Badge variant="outline" className="text-xs">{asset.category}</Badge>
          </TableCell>
        )}
        <TableCell className={`hidden lg:table-cell text-sm text-slate-600 ${colWidths.makeModel || ''}`}>
          {asset.year && `${asset.year} `}{asset.make} {asset.model}
        </TableCell>
        <TableCell className={colWidths.status}>
          <Badge className={`${STATUS_COLORS[asset.status]} border text-xs`}>
            {asset.status}
          </Badge>
        </TableCell>
        <TableCell className={`hidden lg:table-cell text-sm ${colWidths.holder || ''}`}>
          <div className="flex items-center gap-1.5">
            <span>{asset.current_holder || '-'}</span>
            <CheckedOutDaysBadge asset={asset} />
            <SendReminderButton asset={asset} />
          </div>
        </TableCell>
        <TableCell className={colWidths.actions}>
          <div className="flex items-center gap-1">
            {isAvailable(asset) && isFieldUsable(asset, equipmentCategories) ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleOpenCheckout(asset)}
                className="h-8 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                title="Check Out"
                data-testid={`checkout-${asset.id}`}
              >
                <LogOut className="w-4 h-4" />
                <span className="ml-1 hidden sm:inline text-xs">Out</span>
              </Button>
            ) : asset.status === 'Checked Out' && isFieldUsable(asset, equipmentCategories) ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleOpenCheckin(asset)}
                className="h-8 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                title="Check In"
                data-testid={`checkin-${asset.id}`}
              >
                <LogIn className="w-4 h-4" />
                <span className="ml-1 hidden sm:inline text-xs">In</span>
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleViewQR(asset)}
              className="h-8 w-8 p-0"
              title="View QR Code"
            >
              <QrCode className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleViewHistory(asset)}
              className="h-8 w-8 p-0"
              title="View History"
            >
              <History className="w-4 h-4" />
            </Button>
            {canManageAssets && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(asset)}
                  className="h-8 w-8 p-0"
                  title="Edit"
                  data-testid={`edit-asset-${asset.id}`}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedAsset(asset);
                    setShowDeleteDialog(true);
                  }}
                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      subcategory_id: null,
      subcategory_name: '',
      year: '',
      make: '',
      model: '',
      serial_number: '',
      vin: '',
      license_plate: '',
      color: '',
      purchase_date: '',
      notes: '',
      assignable_to_jobs: true,
      field_use: true,
      custom_field_values: {},
      status: 'Active',
      in_shop_note: ''
    });
  };

  // Open create dialog
  const handleCreate = () => {
    resetForm();
    setSelectedAsset(null);
    setShowCreateDialog(true);
  };

  // Open edit dialog
  const handleEdit = (asset) => {
    setSelectedAsset(asset);
    setFormData({
      name: asset.name || '',
      category: asset.category || '',
      subcategory_id: asset.subcategory_id || null,
      subcategory_name: asset.subcategory_name || '',
      year: asset.year?.toString() || '',
      make: asset.make || '',
      model: asset.model || '',
      serial_number: asset.serial_number || '',
      vin: asset.vin || '',
      license_plate: asset.license_plate || '',
      color: asset.color || '',
      purchase_date: asset.purchase_date || '',
      notes: asset.notes || '',
      // Legacy rows may not carry the field yet — default to true so
      // existing equipment keeps showing until Chan actively unchecks.
      assignable_to_jobs: asset.assignable_to_jobs !== false,
      field_use: asset.field_use !== false,
      custom_field_values: asset.custom_field_values || {},
      status: asset.status || 'Active',
      in_shop_note: asset.in_shop_note || ''
    });
    setShowCreateDialog(true);
  };

  // Save asset (create or update)
  const handleSaveAsset = async () => {
    if (!formData.name || !formData.category) {
      toast.error('Name and category are required');
      return;
    }

    // Subcategory is required when the picked parent category has any
    // active subcategories. This makes checklist-template resolution
    // deterministic — no ambiguity about which template a checkout
    // should hit. Owner-signed off (Q3c).
    const availableSubs = subcategoriesForCurrentCategory();
    if (availableSubs.length > 0 && !formData.subcategory_id) {
      toast.error(`Pick a subcategory under "${formData.category}"`);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        year: formData.year ? parseInt(formData.year) : null,
      };

      if (selectedAsset) {
        await axios.put(`${API}/assets/${selectedAsset.id}`, payload);
        toast.success('Equipment updated');
      } else {
        await axios.post(`${API}/assets`, payload);
        toast.success('Equipment created with QR code');
      }
      
      setShowCreateDialog(false);
      resetForm();
      fetchAssets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save asset');
    } finally {
      setSaving(false);
    }
  };

  // Delete asset
  const handleDelete = async () => {
    if (!selectedAsset) return;
    
    try {
      await axios.delete(`${API}/assets/${selectedAsset.id}`);
      toast.success('Equipment deleted');
      setShowDeleteDialog(false);
      setSelectedAsset(null);
      fetchAssets();
    } catch (error) {
      toast.error('Failed to delete asset');
    }
  };

  // Open checkout dialog
  const handleOpenCheckout = async (asset) => {
    setSelectedAsset(asset);
    setSelectedJobId('');
    try {
      // Prefer the subcategory-scoped endpoint when the asset carries
      // one — that's the new source of truth for template linkage.
      // Falls back to the legacy by-category endpoint (which just
      // returns the placeholder) when the asset has no subcategory.
      const url = asset.subcategory_id
        ? `${API}/assets/checkout-checklist-by-subcategory/${asset.subcategory_id}`
        : `${API}/assets/checkout-checklist/${asset.category}`;
      const response = await axios.get(url);
      const flat = flattenItems(response.data.sections);
      setChecklistItems(flat);
      setChecklistTitle(response.data.title || 'Check-Out Checklist');
      setChecklistSource(response.data.source);
      setChecklistUpdatedAt(response.data.updated_at || null);
      // Auto-fill "who's performing this" (logged-in user) and "which
      // asset" (the one just clicked) — see ChecklistRenderer.initialValuesFor.
      setChecklistValues(initialValuesFor(flat, {
        employeeName: user?.full_name,
        assetLabel: asset.asset_number ? `${asset.name} (${asset.asset_number})` : asset.name,
      }));
      setHasSignature(false);
      setShowCheckoutDialog(true);
    } catch (error) {
      toast.error('Failed to load checklist');
    }
  };

  // Open checkin dialog
  const handleOpenCheckin = async (asset) => {
    setSelectedAsset(asset);
    setSelectedJobId('');
    try {
      const url = asset.subcategory_id
        ? `${API}/assets/checkin-checklist-by-subcategory/${asset.subcategory_id}`
        : `${API}/assets/checkin-checklist/${asset.category}`;
      const response = await axios.get(url);
      const flat = flattenItems(response.data.sections);
      setChecklistItems(flat);
      setChecklistTitle(response.data.title || 'Check-In Checklist');
      setChecklistSource(response.data.source);
      setChecklistUpdatedAt(response.data.updated_at || null);
      setChecklistValues(initialValuesFor(flat, {
        employeeName: user?.full_name,
        assetLabel: asset.asset_number ? `${asset.name} (${asset.asset_number})` : asset.name,
      }));
      setHasSignature(false);
      setShowCheckinDialog(true);
      // Link back to the check-out that put this asset in "Checked Out"
      // state and pre-fill the Job it was checked out against — the
      // system already knows this, no reason to make the user re-pick
      // it from scratch. Falls back to a blank/manual pick when there's
      // no last_checkout_id (brand-new asset, or a record that predates
      // this field) or the lookup fails for any reason.
      if (asset.last_checkout_id) {
        try {
          const txResp = await axios.get(`${API}/assets/transactions/${asset.last_checkout_id}`);
          if (txResp.data?.job_id) {
            setSelectedJobId(txResp.data.job_id);
          }
        } catch (e) {
          // No active checkout record found — leave Job blank for manual entry.
        }
      }
    } catch (error) {
      toast.error('Failed to load checklist');
    }
  };

  // Handle checklist item change
  const handleChecklistChange = (index, field, value) => {
    setChecklistValues(prev => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]: value
      }
    }));
  };

  // Handle QR code scan result
  const handleQRScan = (assetId) => {
    setShowScanner(false);
    // Navigate to asset detail page
    navigate(`/asset/${assetId}`);
  };

  // Get signature as base64
  const getSignatureData = () => {
    if (!signatureCanvasRef.current || !hasSignature) return null;
    return signatureCanvasRef.current.toDataURL('image/png');
  };

  // Clear signature
  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setHasSignature(false);
    }
  };

  // Signature drawing handlers
  const startDrawing = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    // The <canvas> has a fixed buffer size (width={400} height={150})
    // but is rendered `w-full`, so the visible CSS size can be 2-3x
    // bigger. Mouse coords are in CSS pixels — we must scale them
    // back into buffer coordinates or the ink lands offset from the
    // pointer. See AssetDetail.js for the same fix + rationale.
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    if (clientX == null || clientY == null) return;
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    if (clientX == null || clientY == null) return;
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  // Submit checkout
  const handleSubmitCheckout = async () => {
    setSaving(true);
    try {
      const checklist = Object.values(checklistValues).map(v => ({
        label: v.label,
        section: v.section_name,
        type: v.type,
        checked: v.checked,
        value: v.value,
        selections: v.selections,
        notes: v.notes,
        photos: v.photos,
        photo: v.photo
      }));

      const signature = getSignatureData();
      
      await axios.post(`${API}/assets/${selectedAsset.id}/checkout`, {
        checklist,
        signature,
        notes: '',
        ...jobPayload()
      });
      
      toast.success('Equipment checked out successfully');
      setShowCheckoutDialog(false);
      fetchAssets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check out asset');
    } finally {
      setSaving(false);
    }
  };

  // Submit checkin
  const handleSubmitCheckin = async () => {
    setSaving(true);
    try {
      const checklist = Object.values(checklistValues).map(v => ({
        label: v.label,
        section: v.section_name,
        type: v.type,
        checked: v.checked,
        value: v.value,
        selections: v.selections,
        notes: v.notes,
        photos: v.photos,
        photo: v.photo
      }));

      const signature = getSignatureData();
      
      await axios.post(`${API}/assets/${selectedAsset.id}/checkin`, {
        checklist,
        signature,
        notes: '',
        ...jobPayload()
      });
      
      toast.success('Equipment checked in successfully');
      setShowCheckinDialog(false);
      fetchAssets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check in asset');
    } finally {
      setSaving(false);
    }
  };

  // View asset history
  const handleViewHistory = async (asset) => {
    setSelectedAsset(asset);
    try {
      const response = await axios.get(`${API}/assets/${asset.id}/history`);
      setAssetHistory(response.data);
      setShowHistoryDialog(true);
    } catch (error) {
      toast.error('Failed to load history');
    }
  };

  // View QR Code. For assets created via the recovery script the stored
  // `qr_code_url` is blank (Cloudinary upload was deliberately skipped to
  // keep the migration fast/offline-safe). Fall back to the live-generation
  // endpoint and turn the PNG bytes into a blob URL so the dialog never
  // shows the empty placeholder for a valid asset.
  const handleViewQR = async (asset) => {
    setSelectedAsset(asset);
    setLiveQrBlobUrl(null);
    setShowQRDialog(true);
    if (asset?.qr_code_url) return;
    try {
      const r = await axios.get(`${API}/assets/${asset.id}/qr-code`, {
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'image/png' });
      setLiveQrBlobUrl(URL.createObjectURL(blob));
    } catch (e) {
      console.error('Live QR fetch failed', e);
    }
  };

  // Download QR Code
  const handleDownloadQR = async (asset) => {
    try {
      const response = await axios.get(`${API}/assets/${asset.id}/qr-code`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'image/png' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Filename combines the auto-generated asset_number (stable, unique)
      // with a sanitized version of the human name so printed stickers
      // and downloaded files are easy to identify at a glance.
      // Example: "TRK-2997A9-T10-Tahoe-LH-qr-code.png"
      const safeName = (asset.name || '')
        .replace(/[^\w\s-]/g, '')   // drop parens, slashes, etc.
        .trim()
        .replace(/\s+/g, '-')       // spaces → dashes
        .replace(/-+/g, '-')        // collapse consecutive dashes ("T10 - Tahoe" → "T10-Tahoe")
        .replace(/^-|-$/g, '');     // trim leading/trailing dashes
      const fileBase = safeName
        ? `${asset.asset_number}-${safeName}`
        : asset.asset_number;
      link.download = `${fileBase}-qr-code.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('QR code downloaded');
    } catch (error) {
      toast.error('Failed to download QR code');
    }
  };

  // Handle photo upload for checklist item
  const handlePhotoUpload = async (index, file) => {
    if (!selectedAsset?.id) {
      toast.error('Select an asset first');
      return;
    }
    setUploadingPhoto(index);
    try {
      // Compress before uploading — same settings as PreCon / Locate
      // so PDFs and OneDrive footprint stay small.
      const compressedBlob = await imageCompression(file, {
        maxSizeMB: 0.3,
        maxWidthOrHeight: 1280,
        initialQuality: 0.82,
        useWebWorker: true,
        fileType: 'image/jpeg',
      });
      const compressedFile = new File(
        [compressedBlob],
        file.name || 'photo.jpg',
        { type: compressedBlob.type || 'image/jpeg', lastModified: Date.now() },
      );
      const authToken = token || localStorage.getItem('fieldflow_token');
      const res = await uploadAssetChecklistPhoto(selectedAsset.id, compressedFile, authToken);
      const url = res?.url;
      if (!url) {
        toast.error('Photo upload failed');
        return;
      }
      setChecklistValues(prev => ({
        ...prev,
        [index]: {
          ...prev[index],
          photos: [...(prev[index]?.photos || []), url],
        },
      }));
      toast.success('Photo added');
    } catch (err) {
      console.error('Photo upload failed:', err);
      toast.error('Photo upload failed');
    } finally {
      setUploadingPhoto(null);
    }
  };

  // Remove photo from checklist
  const handleRemovePhoto = (checklistIndex, photoIndex) => {
    setChecklistValues(prev => ({
      ...prev,
      [checklistIndex]: {
        ...prev[checklistIndex],
        photos: prev[checklistIndex].photos.filter((_, i) => i !== photoIndex)
      }
    }));
  };

  // Dashboard stats
  const stats = {
    total: assets.length,
    available: assets.filter(a => isAvailable(a)).length,
    checkedOut: assets.filter(a => a.status === 'Checked Out').length,
    inService: assets.filter(a => a.status === 'In Service').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="asset-management">
      {/* QR Scanner Modal */}
      {showScanner && (
        <QRScanner 
          onScan={handleQRScan}
          onClose={() => setShowScanner(false)}
        />
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed'] tracking-tight">
            Equipment Management
          </h2>
          <p className="text-slate-500 text-sm mt-1">Track and manage company equipment</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Export Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              window.open(`${API}/assets/export/csv`, '_blank');
            }}
            className="h-9 border-slate-200 text-slate-700 hover:bg-slate-50"
            data-testid="export-assets-btn"
            title="Export to CSV"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          {/* Scan QR Button - visible to all users */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowScanner(true)}
            className="h-9 border-slate-200 text-slate-700 hover:bg-slate-50"
            data-testid="scan-qr-btn"
          >
            <ScanLine className="w-4 h-4 mr-2" />
            Scan QR
          </Button>
          {/* Quick Action Dropdown - for fast check out/in without scrolling */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                className="h-9 bg-slate-900 hover:bg-slate-800 text-white font-bold"
                data-testid="quick-action-btn"
              >
                <Zap className="w-4 h-4 mr-2" />
                Quick Action
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem 
                onClick={() => {
                  setQuickActionMode('checkout');
                  setQuickSearchQuery('');
                  setShowQuickActionModal(true);
                }}
                className="cursor-pointer"
                data-testid="quick-checkout-option"
              >
                <LogOut className="w-4 h-4 mr-2 text-green-600" />
                <span>Quick Check Out</span>
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => {
                  setQuickActionMode('checkin');
                  setQuickSearchQuery('');
                  setShowAllCheckedOut(false);
                  setShowQuickActionModal(true);
                }}
                className="cursor-pointer"
                data-testid="quick-checkin-option"
              >
                <LogIn className="w-4 h-4 mr-2 text-blue-600" />
                <span>Quick Check In</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {canSendReminder && overdueAssetsList.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleSendAllReminders}
              disabled={sendingAllReminders}
              className="h-9 border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="remind-all-overdue-btn"
              title={`Send a reminder to every holder with overdue equipment (${overdueAssetsList.length})`}
            >
              {sendingAllReminders ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
              Remind All Overdue ({overdueAssetsList.length})
            </Button>
          )}
          {canManageAssets && (
            <Button
              size="sm"
              className="h-9 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
              onClick={handleCreate}
              data-testid="create-asset-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Equipment
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-500 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                <p className="text-xs text-slate-500">Total Equipment</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">{stats.available}</p>
                <p className="text-xs text-green-600">Available</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                <LogOut className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-700">{stats.checkedOut}</p>
                <p className="text-xs text-amber-600">Checked Out</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">{stats.inService}</p>
                <p className="text-xs text-blue-600">In Service</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, equipment #, category, make, model, serial, VIN, plate, holder, notes…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="asset-search"
              />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-[150px]" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Checked Out">Checked Out</SelectItem>
                <SelectItem value="In Service">In Service</SelectItem>
                <SelectItem value="Retired">Retired</SelectItem>
              </SelectContent>
            </Select>
            <ViewModeToggle value={viewMode} onChange={setViewMode} />
            {!isEquipmentFiltersDefault && (
              <Button
                variant="ghost"
                onClick={clearEquipmentFilters}
                className="text-slate-500 hover:text-slate-700 whitespace-nowrap"
                data-testid="clear-equipment-filters-btn"
              >
                <X className="w-4 h-4 mr-1" />
                Clear filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Assets Table */}
      {filteredAssets.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-900 mb-1">No assets found</h3>
            <p className="text-slate-500">
              {searchQuery
                ? `Showing 0 of ${assets.length} total equipment — none match "${searchQuery}"`
                : 'Add your first asset to get started'}
            </p>
            {searchQuery && (
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => setSearchQuery('')}
                data-testid="clear-asset-search-btn"
              >
                <X className="w-4 h-4 mr-1" />
                Clear Search
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        viewMode === 'cards' ? (
          assetGroups ? (
            <Accordion
              key="cards"
              type="multiple"
              value={equipmentOpenCats.map(c => `eq-cat-${c}`)}
              onValueChange={(vals) => handleEquipmentOpenChange(vals.map(v => v.replace(/^eq-cat-/, '')))}
              className="space-y-2"
              data-testid="equipment-category-accordion"
            >
              {assetGroups.groupNames.map(catName => {
                const subNames = assetGroups.sortSubs(Object.keys(assetGroups.groups[catName]));
                const soleSub = subNames.length === 1;
                const catTotal = Object.values(assetGroups.groups[catName]).reduce((n, arr) => n + arr.length, 0);
                const openedAt = equipmentOpenCats.includes(catName) ? equipmentOpenedAt[catName] : null;
                return (
                  <AccordionItem
                    key={catName}
                    value={`eq-cat-${catName}`}
                    className="border border-slate-200 rounded-lg overflow-hidden bg-white"
                    data-testid={`equipment-category-group-${catName}`}
                  >
                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-slate-50">
                      <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                        {catName}
                        <span className="text-xs text-slate-400 font-normal">({catTotal})</span>
                        {openedAt && (
                          <span
                            className="text-[11px] font-normal text-amber-700 italic"
                            data-testid={`equipment-opened-note-${catName}`}
                          >
                            Opened by you {(Date.now() - new Date(openedAt).getTime()) < 60000 ? 'just now' : formatDistanceToNow(new Date(openedAt), { addSuffix: true })}
                          </span>
                        )}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 pt-1">
                      {soleSub ? (
                        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                          {assetGroups.groups[catName][subNames[0]].map(renderAssetCardItem)}
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {subNames.map(subName => (
                            <div key={subName} data-testid={`equipment-subcategory-group-${catName}-${subName}`}>
                              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-2">
                                {subName}
                                <span className="text-slate-400 font-normal ml-1.5">({assetGroups.groups[catName][subName].length})</span>
                              </p>
                              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                                {assetGroups.groups[catName][subName].map(renderAssetCardItem)}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {filteredAssets.map(renderAssetCardItem)}
            </div>
          )
        ) : (
          assetGroups ? (
            <Accordion
              key="table"
              type="multiple"
              value={equipmentOpenCats.map(c => `eq-cat-tbl-${c}`)}
              onValueChange={(vals) => handleEquipmentOpenChange(vals.map(v => v.replace(/^eq-cat-tbl-/, '')))}
              className="space-y-2"
              data-testid="equipment-category-accordion-table"
            >
              {assetGroups.groupNames.map(catName => {
                const subNames = assetGroups.sortSubs(Object.keys(assetGroups.groups[catName]));
                const soleSub = subNames.length === 1;
                const catTotal = Object.values(assetGroups.groups[catName]).reduce((n, arr) => n + arr.length, 0);
                const openedAtTbl = equipmentOpenCats.includes(catName) ? equipmentOpenedAt[catName] : null;
                const renderGroupTable = (rows) => (
                  <div className="overflow-x-auto">
                    <Table className="table-fixed">
                      <TableHeader>
                        <TableRow>
                          <TableHead
                            className="w-[18%] cursor-pointer select-none hover:bg-slate-50"
                            onClick={() => toggleSort('asset_number')}
                          >
                            Equipment #<SortIcon col="asset_number" sortBy={sortBy} sortDir={sortDir} />
                          </TableHead>
                          <TableHead
                            className="w-[26%] cursor-pointer select-none hover:bg-slate-50"
                            onClick={() => toggleSort('name')}
                          >
                            Name<SortIcon col="name" sortBy={sortBy} sortDir={sortDir} />
                          </TableHead>
                          <TableHead
                            className="hidden lg:table-cell w-[22%] cursor-pointer select-none hover:bg-slate-50"
                            onClick={() => toggleSort('makemodel')}
                          >
                            Make/Model<SortIcon col="makemodel" sortBy={sortBy} sortDir={sortDir} />
                          </TableHead>
                          <TableHead
                            className="w-[12%] cursor-pointer select-none hover:bg-slate-50"
                            onClick={() => toggleSort('status')}
                          >
                            Status<SortIcon col="status" sortBy={sortBy} sortDir={sortDir} />
                          </TableHead>
                          <TableHead
                            className="hidden lg:table-cell w-[14%] cursor-pointer select-none hover:bg-slate-50"
                            onClick={() => toggleSort('holder')}
                          >
                            Current Holder<SortIcon col="holder" sortBy={sortBy} sortDir={sortDir} />
                          </TableHead>
                          <TableHead className="w-[16%]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rows.map(asset => renderAssetRowItem(asset, { hideCategory: true, colWidths: GROUP_TABLE_COL_WIDTHS }))}
                      </TableBody>
                    </Table>
                  </div>
                );
                return (
                  <AccordionItem
                    key={catName}
                    value={`eq-cat-tbl-${catName}`}
                    className="border border-slate-200 rounded-lg overflow-hidden bg-white"
                    data-testid={`equipment-category-group-table-${catName}`}
                  >
                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-slate-50">
                      <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                        {catName}
                        <span className="text-xs text-slate-400 font-normal">({catTotal})</span>
                        {openedAtTbl && (
                          <span
                            className="text-[11px] font-normal text-amber-700 italic"
                            data-testid={`equipment-opened-note-table-${catName}`}
                          >
                            Opened by you {(Date.now() - new Date(openedAtTbl).getTime()) < 60000 ? 'just now' : formatDistanceToNow(new Date(openedAtTbl), { addSuffix: true })}
                          </span>
                        )}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="px-0 pb-2">
                      {soleSub ? (
                        renderGroupTable(assetGroups.groups[catName][subNames[0]])
                      ) : (
                        <div className="space-y-3">
                          {subNames.map(subName => (
                            <div key={subName} data-testid={`equipment-subcategory-group-table-${catName}-${subName}`}>
                              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1 px-4">
                                {subName}
                                <span className="text-slate-400 font-normal ml-1.5">({assetGroups.groups[catName][subName].length})</span>
                              </p>
                              {renderGroupTable(assetGroups.groups[catName][subName])}
                            </div>
                          ))}
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          ) : (
            <Card className="border-slate-200">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead
                          className="cursor-pointer select-none hover:bg-slate-50"
                          onClick={() => toggleSort('asset_number')}
                          data-testid="sort-asset-number"
                        >
                          Equipment #<SortIcon col="asset_number" sortBy={sortBy} sortDir={sortDir} />
                        </TableHead>
                        <TableHead
                          className="cursor-pointer select-none hover:bg-slate-50"
                          onClick={() => toggleSort('name')}
                          data-testid="sort-name"
                        >
                          Name<SortIcon col="name" sortBy={sortBy} sortDir={sortDir} />
                        </TableHead>
                        <TableHead
                          className="hidden md:table-cell cursor-pointer select-none hover:bg-slate-50"
                          onClick={() => toggleSort('category')}
                          data-testid="sort-category"
                        >
                          Category<SortIcon col="category" sortBy={sortBy} sortDir={sortDir} />
                        </TableHead>
                        <TableHead
                          className="hidden lg:table-cell cursor-pointer select-none hover:bg-slate-50"
                          onClick={() => toggleSort('makemodel')}
                          data-testid="sort-makemodel"
                        >
                          Make/Model<SortIcon col="makemodel" sortBy={sortBy} sortDir={sortDir} />
                        </TableHead>
                        <TableHead
                          className="cursor-pointer select-none hover:bg-slate-50"
                          onClick={() => toggleSort('status')}
                          data-testid="sort-status"
                        >
                          Status<SortIcon col="status" sortBy={sortBy} sortDir={sortDir} />
                        </TableHead>
                        <TableHead
                          className="hidden lg:table-cell cursor-pointer select-none hover:bg-slate-50"
                          onClick={() => toggleSort('holder')}
                          data-testid="sort-holder"
                        >
                          Current Holder<SortIcon col="holder" sortBy={sortBy} sortDir={sortDir} />
                        </TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAssets.map(renderAssetRowItem)}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )
        )
      )}

      {/* Create/Edit Equipment Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-lg">
              {selectedAsset ? 'Edit Equipment' : 'Add New Equipment'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Equipment Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Ford F-150 Work Truck"
                  data-testid="asset-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v) => setFormData({
                    ...formData,
                    category: v,
                    // Category change invalidates any previously-picked
                    // subcategory — it belonged to a different parent.
                    subcategory_id: null,
                    subcategory_name: '',
                  })}
                >
                  <SelectTrigger data-testid="asset-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Subcategory picker — only shown when the picked category
                has active subcategories. Required when visible. Cleared
                automatically whenever Category changes above. */}
            {subcategoriesForCurrentCategory().length > 0 && (
              <div className="space-y-2" data-testid="asset-subcategory-block">
                <Label>Subcategory *</Label>
                <Select
                  value={formData.subcategory_id || ''}
                  onValueChange={(v) => {
                    const sub = equipmentSubcategories.find(s => s.id === v);
                    setFormData({
                      ...formData,
                      subcategory_id: v || null,
                      subcategory_name: sub?.name || '',
                    });
                  }}
                >
                  <SelectTrigger data-testid="asset-subcategory-select">
                    <SelectValue placeholder={`Select a ${formData.category} subcategory`} />
                  </SelectTrigger>
                  <SelectContent>
                    {subcategoriesForCurrentCategory().map(sub => (
                      <SelectItem
                        key={sub.id}
                        value={sub.id}
                        data-testid={`asset-subcategory-option-${sub.id}`}
                      >
                        {sub.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Drives which Check-Out / Check-In / Pre-Trip checklist template runs for this equipment.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Year</Label>
                <Input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({...formData, year: e.target.value})}
                  placeholder="2024"
                />
              </div>
              <div className="space-y-2">
                <Label>Make</Label>
                <Input
                  value={formData.make}
                  onChange={(e) => setFormData({...formData, make: e.target.value})}
                  placeholder="Ford"
                />
              </div>
              <div className="space-y-2">
                <Label>Model</Label>
                <Input
                  value={formData.model}
                  onChange={(e) => setFormData({...formData, model: e.target.value})}
                  placeholder="F-150"
                />
              </div>
              <div className="space-y-2">
                <Label>Color</Label>
                <Input
                  value={formData.color}
                  onChange={(e) => setFormData({...formData, color: e.target.value})}
                  placeholder="White"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>VIN</Label>
                <Input
                  value={formData.vin}
                  onChange={(e) => setFormData({...formData, vin: e.target.value})}
                  placeholder="Vehicle Identification Number"
                />
              </div>
              <div className="space-y-2">
                <Label>Serial Number</Label>
                <Input
                  value={formData.serial_number}
                  onChange={(e) => setFormData({...formData, serial_number: e.target.value})}
                  placeholder="Equipment serial number"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>License Plate</Label>
                <Input
                  value={formData.license_plate}
                  onChange={(e) => setFormData({...formData, license_plate: e.target.value})}
                  placeholder="ABC-1234"
                />
              </div>
              <div className="space-y-2">
                <Label>Purchase Date</Label>
                <Input
                  type="date"
                  value={formData.purchase_date}
                  onChange={(e) => setFormData({...formData, purchase_date: e.target.value})}
                />
              </div>
            </div>

            {/* Aug-2026: Status editing only makes sense for an existing
                asset, and never while it's mid-Checked-Out (that's owned
                by the Check-Out/Check-In flow, not this form). */}
            {selectedAsset && selectedAsset.status !== 'Checked Out' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(v) => setFormData({ ...formData, status: v })}
                  >
                    <SelectTrigger data-testid="asset-status-select">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="In Shop">In Shop</SelectItem>
                      <SelectItem value="Retired">Retired</SelectItem>
                      <SelectItem value="Sold">Sold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.status === 'In Shop' && (
                  <div className="space-y-2">
                    <Label>In Shop Note</Label>
                    <Input
                      value={formData.in_shop_note}
                      onChange={(e) => setFormData({ ...formData, in_shop_note: e.target.value })}
                      placeholder='e.g. "waiting on brake parts"'
                      data-testid="asset-in-shop-note-input"
                    />
                    <p className="text-xs text-slate-500">Shown on the Equipment Overview page's In Shop row. Optional.</p>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={2}
                placeholder="Additional notes about this asset..."
              />
            </div>

            {/* Feb-2026 round 4: Assignable-to-Jobs toggle. Drives the
                Job → Assign Equipment picker filter. Owner unchecks
                personal trucks here; picker is naming-guess-free. */}
            <div className="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50 p-3">
              <input
                id="assignable-to-jobs"
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-slate-400 text-amber-500 focus:ring-amber-500 cursor-pointer"
                checked={formData.assignable_to_jobs !== false}
                onChange={(e) => setFormData({ ...formData, assignable_to_jobs: e.target.checked })}
                data-testid="asset-assignable-to-jobs-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="assignable-to-jobs" className="cursor-pointer font-medium text-slate-800">
                  Available to assign to jobs
                </Label>
                <p className="text-xs text-slate-500 mt-0.5">
                  When checked, this equipment appears in the &quot;Assign Equipment&quot; picker on the Job page. Uncheck for personal trucks or gear that should never be assigned.
                </p>
              </div>
            </div>

            {/* Round 7: per-asset Check-Out/Check-In eligibility —
                mirrors the toggle above. Lets a specific asset (e.g. a
                personal truck) be hidden from the picker even when its
                category (e.g. "Trucks and Vehicles", shared with Flex
                Vehicles) otherwise allows check-out. */}
            <div className="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50 p-3">
              <input
                id="asset-field-use"
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-slate-400 text-amber-500 focus:ring-amber-500 cursor-pointer"
                checked={formData.field_use !== false}
                onChange={(e) => setFormData({ ...formData, field_use: e.target.checked })}
                data-testid="asset-field-use-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="asset-field-use" className="cursor-pointer font-medium text-slate-800">
                  Available for Check-Out/Check-In
                </Label>
                <p className="text-xs text-slate-500 mt-0.5">
                  When checked, this equipment appears in the Check-Out/Check-In picker. Uncheck for personal trucks that shouldn&apos;t be checked out like a shared Flex Vehicle.
                </p>
              </div>
            </div>

            {/* Dynamic Custom Fields based on selected category */}
            {(() => {
              const fields = getCustomFieldsForCategory(formData.category);
              if (!fields.length) return null;
              return (
                <div className="border-t pt-4 space-y-3" data-testid="asset-custom-fields-section">
                  <div>
                    <Label className="text-base">{formData.category} — Custom Fields</Label>
                    <p className="text-xs text-slate-500 mt-1">
                      Configured in Settings → Equipment Categories.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {fields.map((f) => {
                      const cfv = formData.custom_field_values || {};
                      const value = cfv[f.id];
                      const setValue = (v) => setFormData(prev => ({
                        ...prev,
                        custom_field_values: { ...(prev.custom_field_values || {}), [f.id]: v }
                      }));
                      const labelText = f.label + (f.required ? ' *' : '');
                      const testid = `asset-custom-${f.id}`;
                      if (f.field_type === 'date') {
                        const isObj = value && typeof value === 'object';
                        const dateVal = isObj ? (value.date || '') : (value || '');
                        const assigneeVal = isObj ? (value.assignee_id || '') : '';
                        const setDate = (v) => f.requires_assignee ? setValue({ date: v, assignee_id: assigneeVal }) : setValue(v);
                        const setAssignee = (v) => setValue({ date: dateVal, assignee_id: v === '__none__' ? '' : v });
                        return (
                          <div key={f.id} className="space-y-2">
                            <Label>{labelText}</Label>
                            <Input type="date" value={dateVal} onChange={(e) => setDate(e.target.value)} data-testid={testid} />
                            {f.requires_assignee && (
                              <div className="space-y-1 pt-1">
                                <Label className="text-xs text-slate-600">Assigned to</Label>
                                <Select value={assigneeVal || '__none__'} onValueChange={setAssignee}>
                                  <SelectTrigger className="h-9" data-testid={`${testid}-assignee`}>
                                    <SelectValue placeholder="Pick a team member…" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__none__">— Unassigned —</SelectItem>
                                    {teamMembers.map(tm => (
                                      <SelectItem key={tm.id} value={tm.id}>{tm.full_name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            {f.reminder_days_before != null && (
                              <p className="text-xs text-slate-500">Reminder appears on the dashboard {f.reminder_days_before} days before this date.</p>
                            )}
                          </div>
                        );
                      }
                      if (f.field_type === 'number') {
                        return (
                          <div key={f.id} className="space-y-2">
                            <Label>{labelText}</Label>
                            <Input type="number" value={value ?? ''} onChange={(e) => setValue(e.target.value)} data-testid={testid} />
                          </div>
                        );
                      }
                      if (f.field_type === 'dropdown') {
                        return (
                          <div key={f.id} className="space-y-2">
                            <Label>{labelText}</Label>
                            <Select value={value || ''} onValueChange={setValue}>
                              <SelectTrigger data-testid={testid}><SelectValue placeholder="Select…" /></SelectTrigger>
                              <SelectContent>
                                {(f.options || []).map(opt => (
                                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      }
                      if (f.field_type === 'checkbox') {
                        return (
                          <div key={f.id} className="flex items-center gap-2 pt-6">
                            <Switch checked={!!value} onCheckedChange={setValue} data-testid={testid} />
                            <Label>{labelText}</Label>
                          </div>
                        );
                      }
                      return (
                        <div key={f.id} className="space-y-2">
                          <Label>{labelText}</Label>
                          <Input value={value ?? ''} onChange={(e) => setValue(e.target.value)} data-testid={testid} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveAsset}
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                data-testid="save-asset-btn"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {selectedAsset ? 'Save Changes' : 'Create Equipment'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Checkout Dialog */}
      <Dialog open={showCheckoutDialog} onOpenChange={setShowCheckoutDialog}>
        <DialogContent
          className="w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto"
          // Fix (2026-08-30): PhotoLightbox renders as its own overlay
          // outside this DialogContent's DOM subtree — Radix was
          // treating clicks on it as "outside" and closing (wiping)
          // this checkout form behind it. Ignore outside-interaction/
          // escape while the lightbox is open.
          onPointerDownOutside={(e) => { if (lightboxOpen) e.preventDefault(); }}
          onInteractOutside={(e) => { if (lightboxOpen) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (lightboxOpen) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-lg">
              Check Out: {selectedAsset?.name}
            </DialogTitle>
            <DialogDescription>
              Complete the inspection checklist before checking out this asset.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <JobSelectionCard
              value={selectedJobId}
              onChange={setSelectedJobId}
              availableJobs={availableJobs}
              loadingJobs={loadingJobs}
              selectedJob={selectedJob}
              myJobIds={myJobIds}
              testIdPrefix="checkout-job-selection"
              summaryVariant="detailed"
            />
            {/* Checklist Items */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs uppercase font-semibold text-slate-500">Form:</span>
                <span className="text-sm font-medium text-slate-800">{checklistTitle}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${checklistSource === 'template' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                  {checklistSource === 'template' ? 'Linked template' : 'Fallback (no template linked)'}
                </span>
                {checklistSource === 'template' && checklistUpdatedAt && (
                  <span className="text-xs text-slate-400" data-testid="checkout-checklist-updated-at">
                    Updated: {new Date(checklistUpdatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                )}
              </div>
              <ChecklistRenderer
                flatItems={checklistItems}
                values={checklistValues}
                onChange={handleChecklistChange}
                onPhotoUpload={(idx, file) => handlePhotoUpload(idx, file)}
                uploadingPhotoIdx={uploadingPhoto}
                onPhotoRemove={handleRemovePhoto}
                onPhotoClick={openLightbox}
                teamMembers={teamMembers}
                assetsForSelect={assetsForSelect}
                testIdPrefix="checkout"
              />
            </div>

            {/* Signature */}
            <div className="space-y-2">
              <Label className="font-medium">Signature</Label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-2 bg-white">
                <canvas
                  ref={signatureCanvasRef}
                  width={400}
                  height={150}
                  className="w-full border border-slate-200 rounded cursor-crosshair touch-none"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearSignature}
                  className="mt-2 text-slate-500"
                >
                  Clear Signature
                </Button>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowCheckoutDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmitCheckout}
                disabled={saving || !hasSignature || !selectedJobId}
                className="bg-green-600 hover:bg-green-500 text-white"
                data-testid="submit-checkout-btn"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Check Out Equipment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Checkin Dialog - Similar to Checkout */}
      <Dialog open={showCheckinDialog} onOpenChange={setShowCheckinDialog}>
        <DialogContent
          className="w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto"
          // Fix (2026-08-30): same lightbox-outside-click guard as the
          // Checkout dialog above.
          onPointerDownOutside={(e) => { if (lightboxOpen) e.preventDefault(); }}
          onInteractOutside={(e) => { if (lightboxOpen) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (lightboxOpen) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-lg">
              Check In: {selectedAsset?.name}
            </DialogTitle>
            <DialogDescription>
              Complete the return inspection before checking in this asset.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <JobSelectionCard
              value={selectedJobId}
              onChange={setSelectedJobId}
              availableJobs={availableJobs}
              loadingJobs={loadingJobs}
              selectedJob={selectedJob}
              myJobIds={myJobIds}
              testIdPrefix="checkin-job-selection"
              summaryVariant="detailed"
            />
            {/* Current holder info — also the source of the Job pre-fill
                above, via asset.last_checkout_id looked up in handleOpenCheckin. */}
            {selectedAsset?.current_holder && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="checkin-current-holder-banner">
                <p className="text-sm text-amber-800">
                  <span className="font-medium">Originally checked out by:</span>{' '}{selectedAsset.current_holder}
                  {selectedAsset.checked_out_at && (
                    <span className="ml-2 text-amber-600">
                      since {format(new Date(selectedAsset.checked_out_at), 'MMM d, yyyy h:mm a')}
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Checklist Items */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs uppercase font-semibold text-slate-500">Form:</span>
                <span className="text-sm font-medium text-slate-800">{checklistTitle}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${checklistSource === 'template' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                  {checklistSource === 'template' ? 'Linked template' : 'Fallback (no template linked)'}
                </span>
                {checklistSource === 'template' && checklistUpdatedAt && (
                  <span className="text-xs text-slate-400" data-testid="checkin-checklist-updated-at">
                    Updated: {new Date(checklistUpdatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                )}
              </div>
              <ChecklistRenderer
                flatItems={checklistItems}
                values={checklistValues}
                onChange={handleChecklistChange}
                onPhotoUpload={(idx, file) => handlePhotoUpload(idx, file)}
                uploadingPhotoIdx={uploadingPhoto}
                onPhotoRemove={handleRemovePhoto}
                onPhotoClick={openLightbox}
                teamMembers={teamMembers}
                assetsForSelect={assetsForSelect}
                testIdPrefix="checkin"
              />
            </div>

            {/* Signature */}
            <div className="space-y-2">
              <Label className="font-medium">Signature</Label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-2 bg-white">
                <canvas
                  ref={signatureCanvasRef}
                  width={400}
                  height={150}
                  className="w-full border border-slate-200 rounded cursor-crosshair touch-none"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearSignature}
                  className="mt-2 text-slate-500"
                >
                  Clear Signature
                </Button>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowCheckinDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmitCheckin}
                disabled={saving || !hasSignature || !selectedJobId}
                className="bg-blue-600 hover:bg-blue-500 text-white"
                data-testid="submit-checkin-btn"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Check In Equipment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-lg flex items-center justify-between">
              <span>Equipment History: {selectedAsset?.name}</span>
              {selectedAsset && assetHistory.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    window.open(`${API}/assets/export/history-csv?asset_id=${selectedAsset.id}`, '_blank');
                  }}
                >
                  <Download className="w-4 h-4 mr-1" />
                  Export
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {assetHistory.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No history available</p>
            ) : (
              <div className="space-y-3">
                {assetHistory.map((record, idx) => (
                  <div key={idx} className={`p-3 rounded-lg border ${
                    record.type === 'checkout' 
                      ? 'bg-amber-50 border-amber-200' 
                      : 'bg-blue-50 border-blue-200'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {record.type === 'checkout' ? (
                          <LogOut className="w-4 h-4 text-amber-600" />
                        ) : (
                          <LogIn className="w-4 h-4 text-blue-600" />
                        )}
                        <span className="font-medium capitalize">
                          {record.type === 'checkout' ? 'Checked Out' : 'Checked In'}
                        </span>
                      </div>
                      <span className="text-xs text-slate-500">
                        {format(new Date(record.created_at), 'MMM d, yyyy h:mm a')}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      By: {record.user_name}
                      {record.duration_hours && (
                        <span className="ml-2 text-slate-500">
                          (Duration: {record.duration_hours} hours)
                        </span>
                      )}
                    </p>
                    {record.signature_url && (
                      <div className="mt-2">
                        <p className="text-xs text-slate-500 mb-1">Signature:</p>
                        <img 
                          src={record.signature_url} 
                          alt="Signature" 
                          className="h-12 border border-slate-200 rounded bg-white"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={(open) => {
        setShowQRDialog(open);
        if (!open && liveQrBlobUrl) {
          URL.revokeObjectURL(liveQrBlobUrl);
          setLiveQrBlobUrl(null);
        }
      }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-lg text-center">
              QR Code: {selectedAsset?.name ? `${selectedAsset.name} (${selectedAsset.asset_number})` : selectedAsset?.asset_number}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            {(selectedAsset?.qr_code_url || liveQrBlobUrl) ? (
              <img 
                src={selectedAsset?.qr_code_url || liveQrBlobUrl} 
                alt="QR Code"
                className="w-48 h-48 border border-slate-200 rounded-lg"
              />
            ) : (
              <div className="w-48 h-48 bg-slate-100 rounded-lg flex items-center justify-center">
                <QrCode className="w-12 h-12 text-slate-300" />
              </div>
            )}
            <p className="text-sm text-slate-500 text-center">
              Scan to view asset details and check-out/check-in
            </p>
            <Button
              onClick={() => selectedAsset && handleDownloadQR(selectedAsset)}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900"
            >
              <Download className="w-4 h-4 mr-2" />
              Download QR Code
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Equipment?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{selectedAsset?.name}" and all associated records. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Quick Action Modal */}
      <Dialog open={showQuickActionModal} onOpenChange={setShowQuickActionModal}>
        <DialogContent className="w-[calc(100vw-1rem)] max-w-lg max-h-[85vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-4 pt-4 pb-2 border-b">
            <DialogTitle className="font-['Barlow_Condensed'] text-xl flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              {quickActionMode === 'checkout' ? 'Quick Check Out' : 'Quick Check In'}
            </DialogTitle>
            <DialogDescription>
              {quickActionMode === 'checkout' 
                ? 'Select equipment to check out' 
                : 'Select equipment to return'}
            </DialogDescription>
          </DialogHeader>

          {/* Mode Toggle Tabs */}
          <div className="flex border-b px-4">
            <button
              onClick={() => {
                setQuickActionMode('checkout');
                setQuickSearchQuery('');
              }}
              className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-colors ${
                quickActionMode === 'checkout'
                  ? 'border-green-500 text-green-600 bg-green-50/50'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid="quick-action-checkout-tab"
            >
              <LogOut className="w-4 h-4" />
              Check Out
            </button>
            <button
              onClick={() => {
                setQuickActionMode('checkin');
                setQuickSearchQuery('');
                setShowAllCheckedOut(false);
              }}
              className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-colors ${
                quickActionMode === 'checkin'
                  ? 'border-blue-500 text-blue-600 bg-blue-50/50'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid="quick-action-checkin-tab"
            >
              <LogIn className="w-4 h-4" />
              Check In
            </button>
          </div>

          {/* Search and Toggle */}
          <div className="px-4 py-3 space-y-3 border-b bg-slate-50/50">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder={quickActionMode === 'checkout' 
                  ? "Search available equipment..." 
                  : "Search checked out equipment..."}
                value={quickSearchQuery}
                onChange={(e) => setQuickSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="quick-action-search"
                autoFocus
              />
            </div>
            
            {/* Toggles Row */}
            <div className="flex items-center justify-between gap-4">
              {/* Show Recent Toggle - available in both modes */}
              <div className="flex items-center gap-2">
                <Switch
                  checked={showRecentlyUsed}
                  onCheckedChange={toggleRecentlyUsed}
                  data-testid="show-recent-toggle"
                  className="data-[state=checked]:bg-amber-500"
                />
                <div className="flex items-center gap-1 text-sm text-slate-600">
                  <Clock className="w-4 h-4" />
                  <span>Recent</span>
                </div>
              </div>
              
              {/* Show All Toggle - only for Check In mode */}
              {quickActionMode === 'checkin' && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 text-sm text-slate-600">
                    <User className="w-4 h-4" />
                    <span>My Items</span>
                  </div>
                  <Switch
                    checked={showAllCheckedOut}
                    onCheckedChange={setShowAllCheckedOut}
                    data-testid="show-all-toggle"
                  />
                  <div className="flex items-center gap-1 text-sm text-slate-600">
                    <Users className="w-4 h-4" />
                    <span>All</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Equipment List */}
          <div className="flex-1 overflow-y-auto px-4 py-3" style={{ maxHeight: 'calc(85vh - 280px)' }}>
            {(() => {
              // Get recently used assets for this mode
              const recentAssets = getRecentlyUsedAssets();
              
              // Filter assets based on mode
              let filteredItems = [];
              
              // Feb 2026 — shop-only exclusion, extended Round 7 with a
              // per-asset override (personal trucks sharing a category
              // with Flex Vehicles). Shared helper lives in
              // lib/assetStatus.js so the main list uses the identical
              // rule (see filteredAssetGroups below).
              const fieldUsable = (a) => isFieldUsable(a, equipmentCategories);

              if (quickActionMode === 'checkout') {
                // Show only available equipment
                filteredItems = assets.filter(a => isAvailable(a) && fieldUsable(a));
              } else {
                // Check In mode
                if (showAllCheckedOut) {
                  // Show all checked out equipment
                  filteredItems = assets.filter(a => a.status === 'Checked Out' && fieldUsable(a));
                } else {
                  // Show only MY checked out equipment
                  filteredItems = assets.filter(a =>
                    a.status === 'Checked Out' &&
                    a.current_holder_id === user?.id &&
                    fieldUsable(a)
                  );
                }
              }
              
              // Apply search filter
              if (quickSearchQuery) {
                const query = quickSearchQuery.toLowerCase();
                filteredItems = filteredItems.filter(a =>
                  a.name?.toLowerCase().includes(query) ||
                  a.asset_number?.toLowerCase().includes(query) ||
                  a.category?.toLowerCase().includes(query) ||
                  a.make?.toLowerCase().includes(query) ||
                  a.model?.toLowerCase().includes(query)
                );
              }
              
              // Remove recent items from main list to avoid duplicates
              const recentIds = recentAssets.map(a => a.id);
              const mainItems = quickSearchQuery ? filteredItems : filteredItems.filter(a => !recentIds.includes(a.id));

              // Render equipment card helper
              const renderEquipmentCard = (asset, isRecent = false) => {
                const IconComponent = CATEGORY_ICONS[asset.category] || Package;
                return (
                  <button
                    key={asset.id}
                    onClick={() => {
                      addToRecentlyUsed(asset.id);
                      setShowQuickActionModal(false);
                      if (quickActionMode === 'checkout') {
                        handleOpenCheckout(asset);
                      } else {
                        handleOpenCheckin(asset);
                      }
                    }}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all hover:shadow-md active:scale-[0.99] ${
                      isRecent
                        ? 'border-amber-200 hover:border-amber-400 hover:bg-amber-50'
                        : quickActionMode === 'checkout'
                          ? 'border-green-200 hover:border-green-400 hover:bg-green-50'
                          : 'border-blue-200 hover:border-blue-400 hover:bg-blue-50'
                    }`}
                    data-testid={`quick-action-item-${asset.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isRecent
                          ? 'bg-amber-100'
                          : quickActionMode === 'checkout' 
                            ? 'bg-green-100' 
                            : 'bg-blue-100'
                      }`}>
                        <IconComponent className={`w-6 h-6 ${
                          isRecent
                            ? 'text-amber-600'
                            : quickActionMode === 'checkout'
                              ? 'text-green-600'
                              : 'text-blue-600'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-900 truncate">
                            {asset.name}
                          </span>
                          <Badge variant="outline" className="text-xs flex-shrink-0">
                            {asset.category}
                          </Badge>
                          {isRecent && (
                            <Badge className="bg-amber-100 text-amber-700 text-xs flex-shrink-0">
                              Recent
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="font-mono text-xs text-amber-600 font-medium">
                            {asset.asset_number}
                          </span>
                          {asset.year && asset.make && (
                            <span className="text-xs text-slate-500">
                              • {asset.year} {asset.make} {asset.model}
                            </span>
                          )}
                        </div>
                        {quickActionMode === 'checkin' && asset.current_holder && (
                          <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {asset.current_holder}
                            {asset.checkout_time && (
                              <span className="ml-1">
                                • Since {format(new Date(asset.checkout_time), 'MMM d, h:mm a')}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <ArrowRight className={`w-5 h-5 flex-shrink-0 ${
                        isRecent
                          ? 'text-amber-400'
                          : quickActionMode === 'checkout'
                            ? 'text-green-400'
                            : 'text-blue-400'
                      }`} />
                    </div>
                  </button>
                );
              };

              // Empty state
              if (filteredItems.length === 0 && recentAssets.length === 0) {
                return (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-500 font-medium">
                      {quickActionMode === 'checkout' 
                        ? 'No available equipment found'
                        : showAllCheckedOut 
                          ? 'No checked out equipment found'
                          : 'You have no equipment checked out'}
                    </p>
                    {quickActionMode === 'checkin' && !showAllCheckedOut && (
                      <button
                        onClick={() => setShowAllCheckedOut(true)}
                        className="text-blue-600 text-sm mt-2 hover:underline"
                      >
                        Show all checked out items
                      </button>
                    )}
                  </div>
                );
              }

              return (
                <div className="space-y-4">
                  {/* Recently Used Section */}
                  {showRecentlyUsed && recentAssets.length > 0 && !quickSearchQuery && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-4 h-4 text-amber-500" />
                        <span className="text-sm font-medium text-slate-600">Recently Used</span>
                      </div>
                      <div className="space-y-2">
                        {recentAssets.map(asset => renderEquipmentCard(asset, true))}
                      </div>
                    </div>
                  )}
                  
                  {/* Divider if both sections have content */}
                  {showRecentlyUsed && recentAssets.length > 0 && mainItems.length > 0 && !quickSearchQuery && (
                    <div className="flex items-center gap-3 py-1">
                      <div className="flex-1 border-t border-slate-200"></div>
                      <span className="text-xs text-slate-400">All Equipment</span>
                      <div className="flex-1 border-t border-slate-200"></div>
                    </div>
                  )}
                  
                  {/* Main Equipment List — grouped by parent category
                      inside an accordion when there's no search (owner-
                      required build, Feb 25). Search collapses everything
                      into a flat list so results stay one tap away. */}
                  {(quickSearchQuery ? filteredItems : mainItems).length > 0 && (
                    quickSearchQuery ? (
                      <div className="space-y-2">
                        {filteredItems.map(asset => renderEquipmentCard(asset, false))}
                      </div>
                    ) : (() => {
                      // Two-level grouping: parent category → subcategory
                      // → assets. Assets without a subcategory collect
                      // in an "(Unassigned)" bucket inside their parent
                      // so they stay visible. Subcategory sub-groups
                      // sort alphabetically; "(Unassigned)" sinks to
                      // the bottom of its parent.
                      const groups = mainItems.reduce((acc, a) => {
                        const cat = a.category || 'Uncategorized';
                        const sub = a.subcategory_name || '(Unassigned)';
                        acc[cat] = acc[cat] || {};
                        (acc[cat][sub] = acc[cat][sub] || []).push(a);
                        return acc;
                      }, {});
                      const groupNames = Object.keys(groups).sort((x, y) => x.localeCompare(y));
                      const defaultOpen = groupNames.length > 0 ? [`cat-${groupNames[0]}`] : [];
                      const sortSubs = (subKeys) => {
                        // Alphabetical, but push "(Unassigned)" to the
                        // end so tagged equipment leads.
                        return [...subKeys].sort((x, y) => {
                          if (x === '(Unassigned)') return 1;
                          if (y === '(Unassigned)') return -1;
                          return x.localeCompare(y);
                        });
                      };
                      // Category count = total across its subcategories.
                      const catTotal = (cat) => Object.values(groups[cat]).reduce((n, arr) => n + arr.length, 0);
                      return (
                        <Accordion
                          type="multiple"
                          defaultValue={defaultOpen}
                          className="space-y-1"
                          data-testid="checkout-category-accordion"
                        >
                          {groupNames.map(catName => {
                            const subNames = sortSubs(Object.keys(groups[catName]));
                            const soleSub = subNames.length === 1;
                            return (
                              <AccordionItem
                                key={catName}
                                value={`cat-${catName}`}
                                className="border border-slate-200 rounded-lg overflow-hidden"
                                data-testid={`checkout-category-group-${catName}`}
                              >
                                <AccordionTrigger className="px-3 py-2 hover:no-underline hover:bg-slate-50">
                                  <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                                    {catName}
                                    <span className="text-xs text-slate-400 font-normal">({catTotal(catName)})</span>
                                  </span>
                                </AccordionTrigger>
                                <AccordionContent className="px-3 pb-3 pt-1">
                                  {/* When a parent has only ONE subcategory
                                      (or only "(Unassigned)"), skip the
                                      redundant inner label + list assets
                                      flat — matches user's mental model
                                      "don't force a nested layer where
                                      there's nothing to distinguish". */}
                                  {soleSub ? (
                                    <div className="space-y-2">
                                      {groups[catName][subNames[0]].map(asset => renderEquipmentCard(asset, false))}
                                    </div>
                                  ) : (
                                    <div className="space-y-3">
                                      {subNames.map(subName => (
                                        <div key={subName} data-testid={`checkout-subcategory-group-${catName}-${subName}`}>
                                          <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5 pl-1">
                                            {subName}
                                            <span className="text-slate-400 font-normal ml-1.5">({groups[catName][subName].length})</span>
                                          </p>
                                          <div className="space-y-2">
                                            {groups[catName][subName].map(asset => renderEquipmentCard(asset, false))}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </AccordionContent>
                              </AccordionItem>
                            );
                          })}
                        </Accordion>
                      );
                    })()
                  )}
                </div>
              );
            })()}
          </div>

          {/* Footer hint */}
          <div className="px-4 py-3 border-t bg-slate-50 text-center">
            <p className="text-xs text-slate-500">
              Tap an item to {quickActionMode === 'checkout' ? 'check it out' : 'check it in'}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Shared full-size photo viewer for every checklist dialog. */}
      <PhotoLightbox
        open={lightboxOpen}
        photos={lightboxPhotos}
        index={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
        onNavigate={setLightboxIndex}
      />
    </div>
  );
}